import { useState } from 'react';
import type { JSX } from 'react';
import { Button, Modal, cn } from '@taki/ui';
import type { PersonneVue } from '@taki/ui';
import { optionsDe } from './dossier/morceaux.js';
import { jourPur, libelleDuJour } from './dossier/temps.js';

const CLASSES_DU_CHAMP =
  'h-8 rounded-sm border border-border bg-surface px-3 text-sm text-ink-900 focus-visible:border-accent-border focus-visible:outline-none';

/** L'heure proposee par defaut pour une visite planifiee. */
const HEURE_PAR_DEFAUT = 9;

/** Ce que le cadre rend au dossier pour creer la consultation. */
export interface VisiteDemandee {
  /** `null` : maintenant. */
  quand: Date | null;
  motif: string;
  veterinaire: string;
}

/**
 * UNE NOUVELLE VISITE depuis le dossier de l'animal (fondateur, 26/10/2026 :
 * « Consulter maintenant » ET « Planifier »). Le meme cadre a pastilles que le
 * suivi d'une consultation : le motif, quand (planifier seulement), pour quel
 * veterinaire. Maintenant : la consultation s'ouvre aussitot dans l'app
 * Consultations. Planifier : elle est creee « Prevue » et reste sur le dossier.
 *
 * ELLE S'OUVRE DANS UNE FENETRE (`Modal`, G6 : la fenetre est la place d'un
 * geste d'action) : ses boutons de validation vivent dans son pied, le corps
 * du dossier n'en porte aucun.
 */
export function NouvelleVisite({
  mode,
  personnes,
  veterinaireParDefaut,
  erreur = null,
  onCreer,
  onAnnuler,
}: {
  mode: 'maintenant' | 'planifier';
  personnes: readonly PersonneVue[];
  veterinaireParDefaut: string;
  /** Pourquoi la creation a echoue, dite dans la fenetre quand l'appelant n'a pas de place ailleurs. */
  erreur?: string | null;
  onCreer: (visite: VisiteDemandee) => Promise<boolean>;
  onAnnuler: () => void;
}): JSX.Element {
  const motifs = optionsDe('consultation', 'reason');
  const [motif, setMotif] = useState('');
  const [veterinaire, setVeterinaire] = useState(veterinaireParDefaut);
  const [jour, setJour] = useState(() => jourPur(premierCreneau()));
  const [heure, setHeure] = useState(`${String(HEURE_PAR_DEFAUT).padStart(2, '0')}:00`);
  const [enCours, setEnCours] = useState(false);
  const equipe = personnes.filter((p) => p.statut === 'actif' || p.personId === veterinaire);

  const quand = mode === 'maintenant' ? null : instantDe(jour, heure);
  const pret = motif !== '' && (mode === 'maintenant' || quand !== null);
  const creer = async (): Promise<void> => {
    if (!pret || enCours) return;
    setEnCours(true);
    await onCreer({ quand, motif, veterinaire });
    setEnCours(false);
  };

  return (
    <Modal
      open
      onClose={onAnnuler}
      title={mode === 'maintenant' ? 'Consulter maintenant' : 'Planifier une visite'}
      footer={
        <>
          <Button variant="secondary" onClick={onAnnuler}>
            Annuler
          </Button>
          <span title={pret ? undefined : 'Choisis un motif.'} className={cn(!pret && 'cursor-not-allowed')}>
            <Button disabled={!pret || enCours} onClick={() => void creer()}>
              {mode === 'maintenant' ? 'Ouvrir la consultation' : 'Planifier'}
            </Button>
          </span>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {erreur === null ? null : <p className="text-sm text-danger">{erreur}</p>}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-ink-500">Motif</span>
          <div className="flex flex-wrap gap-2">
            {motifs.map((o) => (
              <Button key={o.value} size="sm" variant={o.value === motif ? 'dark' : 'secondary'} onClick={() => setMotif(o.value)}>
                {o.label}
              </Button>
            ))}
          </div>
        </div>
        {mode === 'planifier' ? (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-ink-500">Quand</span>
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" aria-label="Jour de la visite" className={CLASSES_DU_CHAMP} value={jour} min={jourPur(new Date())} onChange={(e) => setJour(e.target.value)} />
              <input type="time" aria-label="Heure de la visite" className={cn(CLASSES_DU_CHAMP, 'w-28')} value={heure} step={300} onChange={(e) => setHeure(e.target.value)} />
            </div>
          </div>
        ) : null}
        {equipe.length === 0 ? null : (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-ink-500">Vétérinaire</span>
            <div className="flex flex-wrap gap-2">
              {equipe.map((p) => (
                <Button key={p.personId} size="sm" variant={p.personId === veterinaire ? 'dark' : 'secondary'} onClick={() => setVeterinaire(p.personId)}>
                  {p.displayName || p.email}
                </Button>
              ))}
            </div>
          </div>
        )}
        {quand === null ? null : (
          <p className="text-sm text-ink-900">
            Prévue le {libelleDuJour(quand).toLowerCase()} {quand.getFullYear()} à {heure}.
          </p>
        )}
      </div>
    </Modal>
  );
}

/** Le prochain 09:00, le creneau propose par defaut : une visite planifiee ne tombe jamais dans le passe. */
function premierCreneau(): Date {
  const date = new Date();
  if (date.getHours() >= HEURE_PAR_DEFAUT) date.setDate(date.getDate() + 1);
  return date;
}

/** Un jour `YYYY-MM-DD` et une heure `HH:MM` en instant local ; `null` si l'un manque. */
function instantDe(jour: string, heure: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(jour) || !/^\d{2}:\d{2}$/.test(heure)) return null;
  const date = new Date(Number(jour.slice(0, 4)), Number(jour.slice(5, 7)) - 1, Number(jour.slice(8, 10)), Number(heure.slice(0, 2)), Number(heure.slice(3, 5)));
  return Number.isNaN(date.getTime()) ? null : date;
}
